// Pure read helpers for the Fact check screen (R11 patrol + R10 entity check).
//
// The screen never re-decides what the back end decided. Two rules carry most of this file:
//
//  1. **A column we did not read can never be drawn as agreement.** `fact.unread_columns` is the
//     back end's own list, and every cell in it is `unreadable` regardless of what is in `cells` —
//     which is what keeps "we could not open your Crunchbase page" from rendering as a green dot.
//  2. **Disagreement is the issue ledger's word, not a string compare.** `issues_open` already
//     knows that "Notion Labs, Inc." and "Notion" are the same company; comparing cell values in
//     the browser would paint a mismatch the back end deliberately refused to raise.

export const AUTHORITY_COLUMN = "website";

export const FACT_COLUMNS = ["website", "geo_site", "ai_says", "linkedin", "crunchbase"];

export const COLUMN_LABELS = {
  website: "Your website",
  geo_site: "Your GEO site",
  ai_says: "What AI says",
  linkedin: "LinkedIn",
  crunchbase: "Crunchbase",
};

/** The four states a cell can be in. `unreadable` is the one that must never look like agreement. */
export const CELL_STATES = ["match", "mismatch", "missing", "unreadable"];

/** Short words for a column we could not read, keyed by the back end's own source states. */
export const SOURCE_LABELS = {
  missing_profile: "Not linked",
  fetch_failed: "Could not read",
  no_run: "No diagnosis yet",
  no_values: "Nothing to read",
  no_site: "No GEO site yet",
};

export function sourceState(patrol, column) {
  return String((patrol?.sources || {})[column]?.state || "ok");
}

export function sourceReason(patrol, column) {
  return String((patrol?.sources || {})[column]?.reason || "");
}

export function sourceLabel(patrol, column) {
  const state = sourceState(patrol, column);
  return state === "ok" ? "" : SOURCE_LABELS[state] || "Not checked";
}

function unread(fact, patrol, column) {
  const listed = Array.isArray(fact?.unread_columns) && fact.unread_columns.includes(column);
  return listed || sourceState(patrol, column) !== "ok";
}

export function cellValue(fact, column) {
  return String(((fact?.cells || {})[column] || {}).value || "").trim();
}

export function cellQuote(fact, column) {
  return String(((fact?.cells || {})[column] || {}).quote || "").trim();
}

export function cellUrl(fact, column) {
  return String(((fact?.cells || {})[column] || {}).source_url || "").trim();
}

export function issueFor(patrol, key, column) {
  return (patrol?.issues_open || []).find((issue) => issue.key === key && issue.column === column) || null;
}

export function cellState(fact, column, patrol) {
  if (unread(fact, patrol, column)) return "unreadable";
  if (!cellValue(fact, column)) return "missing";
  if (column === AUTHORITY_COLUMN) return "match";
  return issueFor(patrol, fact?.key, column) ? "mismatch" : "match";
}

/** Open issues that are still waiting on somebody. `unverified` ones are carried, not found. */
export function openIssues(patrol) {
  return (patrol?.issues_open || []).filter((issue) => !issue.unverified);
}

export function carriedIssues(patrol) {
  return (patrol?.issues_open || []).filter((issue) => issue.unverified);
}

// ── R10: the entity check, in the three segments the profile card uses ──────────────────────────

/**
 * `{ agreed, attention, unstated }` — one segment per thing the client can do about a fact.
 *
 * `partial: true` means the fact was absent from the part of the site we managed to read, which is
 * not the same claim as "your site does not say" and is labelled differently downstream.
 */
export function entitySegments(check) {
  const facts = Array.isArray(check?.facts) ? check.facts : [];
  return {
    agreed: facts.filter((fact) => fact.verdict === "match"),
    attention: facts.filter((fact) => fact.verdict === "mismatch"),
    unstated: facts.filter((fact) => fact.verdict === "missing"),
  };
}

export function websiteUnreadable(check) {
  return String(check?.website?.state || "ok") !== "ok";
}

export function entityFactLabel(fact) {
  return String(fact?.label || fact?.key || "");
}

export function entityColumnValue(fact, column) {
  return String((fact?.[column] || {}).value || "").trim();
}

export function entityColumnUrl(fact, column) {
  return String((fact?.[column] || {}).source_url || "").trim();
}

/** The one sentence under a fact the client has to act on. */
export function entityDetail(fact) {
  const site = entityColumnValue(fact, "website");
  const geo = entityColumnValue(fact, "geo_site");
  const ai = entityColumnValue(fact, "ai_says");
  if (fact?.verdict === "mismatch") {
    const parts = [];
    if (site) parts.push(`your website says “${site}”`);
    if (geo && geo !== site) parts.push(`your GEO site says “${geo}”`);
    if (ai && ai !== site) parts.push(`AI says “${ai}”`);
    return `${parts.join("; ")}.`;
  }
  if (fact?.verdict === "missing") {
    if (fact?.partial) return "We only read part of your site and did not find this — it may still be there.";
    if (!site) return "Your own website does not state this, so there is nothing for the others to agree with.";
    return "Your website states it; the pages AI reads do not.";
  }
  return site ? `All three say “${site}”.` : "";
}

export function missingPlatforms(check) {
  return (check?.same_as || []).filter((row) => !row.present);
}

export function presentPlatforms(check) {
  return (check?.same_as || []).filter((row) => row.present);
}

/**
 * An href, or nothing. Every URL on this screen was read off somebody else's page, so a scheme
 * that is not http(s) — `javascript:`, `data:` — must never reach an `href`.
 */
export function safeHref(url) {
  const value = String(url || "").trim();
  return /^https?:\/\//i.test(value) ? value : "";
}

export function ago(timestamp) {
  const seconds = Number(timestamp) ? Date.now() / 1000 - Number(timestamp) : 0;
  if (!seconds || seconds < 0) return "just now";
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}
